import { memo, useContext, useEffect, useState } from 'react'

// Material UI
import {
  InputLabel,
  Stack,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  useTheme,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Checkbox
} from '@mui/material'

import { Close } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { createGalerias } from '../services/galerias.services'

// form validation
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { GaleriasContext, IDimension, IGalerias } from '../context/galerias.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import ValidarDireccion from '../services/validacionesDir'

import Loading from '../../../components/Common/Loading'
import { TenantsContext } from '../../../context/tenants.context'

const MAX_NOMBRE_LENGTH = 75
const MAX_DIR_LENGTH = 150
const MAX_DIM_LENGTH = 50

export interface CreaGaleriaProps {
  open: boolean
  setOpen: (open: boolean) => void
  dimensiones: IDimension[]
}

export interface SelectedDim {
  activo: boolean
  id: number
  nombre: string
  valorDefault: string
  obligatorio: boolean
}

const CreaGalerias = ({ open, setOpen, dimensiones }: CreaGaleriaProps) => {
  const [loading, setLoading] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // temporal data
  const [nombre, setNombre] = useState<string>('')
  const [center, setCenter] = useState<string>('')
  const [direccionValidada, setDireccionValidada] = useState<boolean>(false)
  const [latitud, setLatitud] = useState<number>(0)
  const [longitud, setLongitud] = useState<number>(0)
  const [selectedDim, setSelectedDim] = useState<SelectedDim[]>([])
  const [errDims, setErrDims] = useState<{ id: number; error: string }[]>(null)
  const { fetchData } = useContext(GaleriasContext)
  const { tenant } = useContext(TenantsContext)

  const schema: yup.AnyObjectSchema = yup.object({
    nombreGaleria: yup
      .string()
      .required('El nombre es obligatorio')
      .typeError('El nombre es requerido')
      .max(MAX_NOMBRE_LENGTH, `El nombre no puede superar los ${MAX_NOMBRE_LENGTH} caracteres`),
    direcGaleria: yup
      .string()
      .required('La direccion es obligatoria')
      .typeError('La dirección es requerida')
      .max(MAX_DIR_LENGTH, `La dirección no puede superar los ${MAX_DIR_LENGTH} caracteres`)
  })

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
    setError,
    //setFocus,
    clearErrors
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur'
  })

  const validarDimensiones = (): boolean => {
    const errores: Array<{ id: number; error: string }> = []

    selectedDim.forEach((dim) => {
      if (!dim.activo) return

      if (!dim.valorDefault?.trim()) {
        errores.push({ id: dim.id, error: 'Debes agregar un valor' })
        return
      }

      const dimension = dimensiones.find((d: IDimension) => d.idGaleriaEstructuraDimension === dim.id)

      if (dimension?.tipoDato === 'int') {
        if (isNaN(Number(dim.valorDefault)) || !Number.isInteger(Number(dim.valorDefault))) {
          errores.push({ id: dim.id, error: 'El valor debe ser un número entero' })
        }
      }
    })

    setErrDims(errores)
    return Object.keys(errores).length === 0
  }

  const onSubmit = async (data: any) => {
    if (!direccionValidada) {
      setError('direcGaleria', {
        message: 'Debes validar la dirección',
        type: 'manual'
      })
      return SnackBarUtils.error('Debes validar la dirección ingresada')
    }
    if (errDims?.length > 0) return

    const galeria: IGalerias = {
      nombre: data.nombreGaleria,
      direccion: data.direcGaleria,
      latitud: latitud,
      longitud: longitud,
      estructura: selectedDim
        .filter((dim: SelectedDim) => dim.activo)
        .map((dim: SelectedDim) => ({
          idGaleriaEstructuraDimension: dim.id,
          valorDefault: dim.valorDefault,
          obligatorio: dim.obligatorio
        }))
    }

    try {
      setLoading(true)
      await createGalerias(galeria, tenant)
      fetchData()

      SnackBarUtils.success('Galería creada con éxito')
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Ha ocurrido un error al crear la galería')
    } finally {
      handleClose()
      setLoading(false)
      setErrDims(null)
    }
  }

  useEffect(() => {
    const selected: SelectedDim[] = dimensiones
      .map((dim: IDimension) => ({
        activo: dim.usarPorDefault,
        id: dim.idGaleriaEstructuraDimension,
        nombre: dim.nombre,
        valorDefault: '',
        obligatorio: false
      }))
      .sort((a, b) => {
        // Primero las activas (true viene antes que false)
        if (b.activo !== a.activo) {
          return b.activo ? 1 : -1
        }
        // Si tienen el mismo estado activo, mantener orden original
        return 0
      })

    setSelectedDim(selected)
  }, [dimensiones])

  const handleClose = async () => {
    setOpen(false)
    setNombre('')
    setErrDims(null)
    setSelectedDim([])
    setCenter('')
    setDireccionValidada(false)
    setLatitud(0)
    setLongitud(0)
    reset()
  }

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.75)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography variant="h5">Guardando Galería</Typography>
        <Loading color="inherit" />
      </Box>
    )
  }

  return (
    <>
      <Dialog maxWidth={isMobile ? 'lg' : 'sm'} fullWidth fullScreen={isMobile} open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ overflowX: 'hidden' }}>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            Agregar una nueva Galería
            {open ? (
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500]
                }}
              >
                <Close />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            <Stack direction="column" sx={{ margin: '20px 0' }} spacing="20px">
              <Stack sx={{ width: '100%' }}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  Nombre de la Galería
                  <Typography variant="subtitle2">
                    {nombre.length || 0}/{MAX_NOMBRE_LENGTH}
                  </Typography>
                </InputLabel>
                <TextField
                  disabled={loading}
                  sx={{ width: '100%' }}
                  error={!!errors.nombreGaleria}
                  helperText={errors?.nombreGaleria && String(errors?.nombreGaleria?.message)}
                  variant="outlined"
                  {...register('nombreGaleria')}
                  onChange={(e) => setNombre(e.target.value)}
                  inputProps={{
                    maxLength: MAX_NOMBRE_LENGTH
                  }}
                />
              </Stack>
              <ValidarDireccion
                loading={loading}
                register={register}
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                center={center}
                setCenter={setCenter}
                setError={setError}
                clearErrors={clearErrors}
                setLongitud={setLongitud}
                setLatitud={setLatitud}
                setDireccionValidada={setDireccionValidada}
              />
            </Stack>

            <InputLabel>Dimensiones</InputLabel>
            {selectedDim.map((dim: SelectedDim, i: number) => {
              const err = errDims?.find((e) => e.id === dim.id)
              return (
                <>
                  <InputLabel
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2rem',
                      width: '100%',
                      marginTop: '1rem'
                    }}
                  >
                    {dim.nombre}
                    <Typography variant="subtitle2">
                      {dim.valorDefault?.length || 0}/{MAX_DIM_LENGTH}
                    </Typography>
                  </InputLabel>
                  <Stack
                    key={i}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ width: '100%', marginTop: '1rem' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dim.activo}
                          onChange={(e) => {
                            const newSelected = [...selectedDim]
                            newSelected[i].activo = e.target.checked
                            if (!e.target.checked) {
                              setErrDims((prev) => prev?.filter((err) => err.id !== dim.id) ?? null)
                            }
                            setSelectedDim(newSelected)
                          }}
                          disabled={loading}
                        />
                      }
                      label="Activo"
                    />

                    <TextField
                      value={dim.valorDefault}
                      onChange={(e) => {
                        const newSelected = [...selectedDim]
                        newSelected[i].valorDefault = e.target.value
                        setSelectedDim(newSelected)
                      }}
                      onBlur={validarDimensiones}
                      error={!!err}
                      helperText={dim.activo && (err?.error || '')}
                      variant="outlined"
                      placeholder="Valor default"
                      disabled={loading || !dim.activo}
                      sx={{ flexGrow: 1 }}
                      inputProps={{ maxLength: MAX_DIM_LENGTH }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dim.obligatorio}
                          onChange={(e) => {
                            const newSelected = [...selectedDim]
                            newSelected[i].obligatorio = e.target.checked
                            setSelectedDim(newSelected)
                          }}
                          disabled={loading || !dim.activo}
                        />
                      }
                      label="Obligatorio"
                    />
                  </Stack>
                </>
              )
            })}
          </DialogContent>
          <DialogActions
            sx={{
              diplay: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-end',
              justifyContent: 'space-between',
              width: '100%',
              padding: '22px',
              paddingTop: 0,
              gap: '8px',
              position: 'relative'
            }}
          >
            <Button color="inherit" onClick={handleClose}>
              Volver
            </Button>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" type="submit" disabled={loading}>
                {loading ? 'Grabando' : 'Grabar'}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}

export default memo(CreaGalerias)
